<script setup>
import SaveServerAction from "@frontJs/components/actions/projectManagement/dev/SaveServerAction.vue";
import ServerTable from "@frontJs/components/tables/projectManagement/dev/ServerTable.vue";
import TenantServerTable from "@frontJs/components/tables/projectManagement/dev/TenantServerTable.vue";
import ProjectManagementLayout from "@frontJs/layouts/ProjectManagementLayout.vue";
import {
  DocumentIcon,
  PencilIcon,
  CalendarIcon,
  CurrencyEuroIcon,
  MapPinIcon,
  CogIcon,
  DocumentTextIcon,
  CircleStackIcon,
  ServerIcon,
  ShieldCheckIcon,
  CheckIcon,
  NoSymbolIcon,
  KeyIcon
} from "@heroicons/vue/24/outline";
import { Head, router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import PageTitle from "@js/components/titles/PageTitle.vue";
import DisplayContentAction from "@js/components/actions/DisplayContentAction.vue";
import TwoLineCardData from "@js/components/cards/TwoLineCardData.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { formatPrice } from "@js/utils/formatter";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { servers } = defineProps({
  servers: {
    type: Array,
    required: true,
  },
});

const { t } = useI18n();

const selectedTab = ref(0);

function changeTab(index) {
  selectedTab.value = index;
}

function updateData() {
  router.reload();
}

function getBackupContent(server) {
  let content = "";

  if (server.backupHost) {
    content +=
      "<h2>" +
      t("title.host") +
      "</h2><ul><li>" +
      server.backupHost +
      "</li></ul>";
  }

  if (server.backupLogin) {
    content +=
      "<h2>" +
      t("title.username") +
      "</h2><ul><li>" +
      server.backupLogin +
      "</li></ul>";
  }

  if (server.backupPassword) {
    content +=
      "<h2>" +
      t("title.password") +
      "</h2><ul><li>" +
      server.backupPassword +
      "</li></ul>";
  }

  if (server.backupStorageSpace) {
    content +=
      "<h2>" +
      t("title.storageSpace") +
      "</h2><ul><li>" +
      server.backupStorageSpace +
      "</li></ul>";
  }

  return content.length === 0 ? "N/A" : content;
}

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
  <Head>
    <title>
      {{ t("title.servers") }} - {{ t("title.dev") }} -
      {{ t("title.projectManagement") }} - {{ t("title.datakepler") }}
    </title>
  </Head>

  <PageTitle>
    {{ t("title.projectManagement") }} - {{ t("title.dev") }} -
    {{ t("title.servers") }}
  </PageTitle>

  <TabGroup
    :selected-tab="selectedTab"
    :change-tab="changeTab"
    :tabs="{
      ...servers.reduce((acc, server, index) => {
        acc[server.name.toLowerCase().replace(/\s/g, '')] = server.name;
        return acc;
      }, {}),
    }"
  >
    <template #panels>
      <TabPanel
        v-for="(server, index) in servers"
        :key="index"
        without-background
        unmount
      >
        <div class="mb-0 flex justify-end">
          <SaveServerAction :server="server" :callback="updateData">
            <template #button="{ setIsOpen }">
              <ActionButton
                size="small"
                type="primary"
                :callback="() => setIsOpen(true)"
                :title="t('button.edit')"
                :left-icon="PencilIcon"
              >
                {{ t("button.editServer") }}
              </ActionButton>
            </template>
          </SaveServerAction>
        </div>

        <div class="mb-4 flex flex-col gap-2">
          <div class="relative border border-main rounded-lg p-4 mt-6">
            <div class="absolute -top-4 left-2 bg-indigo-600 text-white px-3 py-0.5 text-sm rounded shadow" style="line-height: 1.25rem;">
              {{ t('label.generalInformation') }}
            </div>

            <div
              class="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7"
            >
              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.serverName')"
                :icon="ServerIcon"
              >
                <p class="text-sm">{{ server.serverName ?? "N/A" }}</p>
              </TwoLineCardData>

              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.commitment')"
                :icon="CalendarIcon"
              >
                <p class="text-sm">{{ server.commitment ?? "N/A" }}</p>
              </TwoLineCardData>

              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.ips')"
                :icon="MapPinIcon"
              >
                <p class="text-sm">{{ server.ips ?? "N/A" }}</p>
              </TwoLineCardData>

              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.ipBlock')"
                :icon="MapPinIcon"
              >
                <DisplayContentAction
                  :title="t('title.ipBlock')"
                  :content="server.ipBlock ?? 'N/A'"
                >
                  <template #button="{ setIsOpen }">
                    <ActionButton
                      size="small"
                      :title="t('button.seeIpBlock')"
                      type="primary"
                      :callback="() => setIsOpen(true)"
                    >
                    {{ t("button.seeDetails") }}
                    </ActionButton>
                  </template>
                </DisplayContentAction>
              </TwoLineCardData>

              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.phpVersions')"
                :icon="CogIcon"
              >
                <p class="text-sm">{{ server.phpVersions ?? "N/A" }}</p>
              </TwoLineCardData>

              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.backup')"
                :icon="CircleStackIcon"
              >
                <DisplayContentAction
                  v-if="server.id !== 8"
                  :title="t('title.backup')"
                  :content="getBackupContent(server)"
                >
                  <template #button="{ setIsOpen }">
                    <ActionButton
                      size="small"
                      :title="t('button.seeBackup')"
                      type="primary"
                      :callback="() => setIsOpen(true)"
                    >
                      {{ t("button.seeDetails") }}
                    </ActionButton>
                  </template>
                </DisplayContentAction>
              </TwoLineCardData>

              <TwoLineCardData
                :title="t('title.additionalInformation')"
                :icon="DocumentIcon"
              >
                <DisplayContentAction
                  :title="t('title.additionalInformation')"
                  :content="server.additionalInformation ?? 'N/A'"
                >
                  <template #button="{ setIsOpen }">
                    <ActionButton
                      size="small"
                      :title="t('button.seeAdditionalInformation')"
                      type="primary"
                      :callback="() => setIsOpen(true)"
                    >
                      {{ t("button.seeDetails") }}
                    </ActionButton>
                  </template>
                </DisplayContentAction>
              </TwoLineCardData>
            </div>
          </div>
          <div class="relative border border-main rounded-lg p-4 mt-6">
            <div class="absolute -top-4 left-2 bg-indigo-600 text-white px-3 py-0.5 text-sm rounded shadow" style="line-height: 1.25rem;">
              {{ t('label.security') }}
            </div>
            <div class="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6">
              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.sshLimitedByIp')"
                :icon="ShieldCheckIcon"
              >
                <CheckIcon class="size-6 text-green-600" v-if="server.isSshLimitedByIp"/>
                <NoSymbolIcon class="size-6 text-red-600"  v-else />
              </TwoLineCardData>
              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.sshProtectedByKey')"
                :icon="KeyIcon"
              >
                <CheckIcon class="size-6 text-green-600" v-if="server.isSshProtectedByKey"/>
                <NoSymbolIcon class="size-6 text-red-600"  v-else />
              </TwoLineCardData>
              <TwoLineCardData
                v-if="server.id !== 8"
                :title="t('title.serverAdminLimitedByIp')"
                :icon="ShieldCheckIcon"
              >
                <p class="text-sm">
                  <CheckIcon class="size-6 text-green-600" v-if="server.isServerAdminLimitedByIp"/>
                  <NoSymbolIcon class="size-6 text-red-600"  v-else />
                </p>
              </TwoLineCardData>
            </div>
          </div>
        </div>

        <TenantServerTable :server="server" />
      </TabPanel>
    </template>
  </TabGroup>
</template>
